
/**
 * Utility to generate a printable HTML report for a cash session.
 * PREMIUM REDESIGN: Modern typography, summary cards, and clean aesthetics.
 */
export const generateReporteCajaHTML = (data) => {
    const { sesion, recaudacion, gastos, depositos } = data;
    const fechaReporte = sesion.fecha ? new Date(sesion.fecha).toLocaleDateString('es-PE', {
        day: '2-digit', month: 'long', year: 'numeric'
    }) : '-';

    const horaReporte = sesion.fecha ? new Date(sesion.fecha).toLocaleTimeString('es-PE', {
        hour: '2-digit', minute: '2-digit'
    }) : '-';

    // Format currency helper
    const fmt = (val) => `S/ ${parseFloat(val || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Calculate totals for tables
    const totalRecaudacion = recaudacion.reduce((acc, curr) => acc + curr.monto, 0);
    const totalGastos = gastos.reduce((acc, curr) => acc + curr.monto, 0);
    const totalDepositos = depositos.reduce((acc, curr) => acc + curr.monto, 0);

    return `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Reporte de Caja - ${sesion.usuario}</title>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary: #2563eb;
            --success: #10b981;
            --danger: #ef4444;
            --warning: #f59e0b;
            --text-main: #1f2937;
            --text-muted: #6b7280;
            --bg-light: #f9fafb;
            --border: #e5e7eb;
        }

        body {
            font-family: 'Inter', system-ui, -apple-system, sans-serif;
            font-size: 11px;
            color: var(--text-main);
            margin: 0;
            padding: 40px;
            line-height: 1.5;
            background: white;
        }

        /* Print Settings */
        @media print {
            body { padding: 0; margin: 0; }
            .no-print { display: none; }
            @page { margin: 1cm; }
        }

        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid var(--border);
            padding-bottom: 20px;
            margin-bottom: 30px;
        }

        .header-title h1 {
            font-size: 24px;
            font-weight: 700;
            margin: 0;
            color: var(--text-main);
            letter-spacing: -0.025em;
        }

        .header-title p {
            font-size: 12px;
            color: var(--text-muted);
            margin: 4px 0 0 0;
        }

        .header-meta {
            text-align: right;
        }

        .badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 9999px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            background: #dbeafe;
            color: #1e40af;
        }

        /* Summary Cards */
        .summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 30px;
        }

        .card {
            padding: 15px;
            border: 1px solid var(--border);
            border-radius: 12px;
            background: var(--bg-light);
        }

        .card-label {
            font-size: 10px;
            font-weight: 600;
            color: var(--text-muted);
            text-transform: uppercase;
            margin-bottom: 4px;
        }

        .card-value {
            font-size: 16px;
            font-weight: 700;
        }

        .value-success { color: var(--success); }
        .value-danger { color: var(--danger); }
        .value-warning { color: var(--warning); }
        .value-primary { color: var(--primary); }

        /* Tables */
        .section-title {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 12px;
            padding-left: 8px;
            border-left: 4px solid var(--primary);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid var(--border);
        }

        th {
            background: #f8fafc;
            color: var(--text-muted);
            font-weight: 600;
            text-transform: uppercase;
            font-size: 9px;
            padding: 10px 12px;
            text-align: left;
            border-bottom: 1px solid var(--border);
        }

        td {
            padding: 10px 12px;
            border-bottom: 1px solid var(--border);
            vertical-align: middle;
        }

        tr:last-child td {
            border-bottom: none;
        }

        tr:nth-child(even) {
            background-color: #fcfcfc;
        }

        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 700; }

        .total-row {
            background: #f1f5f9 !important;
            font-weight: 700;
        }

        .footer {
            margin-top: 50px;
            text-align: center;
            font-size: 10px;
            color: var(--text-muted);
            border-top: 1px solid var(--border);
            padding-top: 20px;
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="header-title">
            <h1>Reporte de Caja</h1>
            <p>SESIÓN #${sesion.id} &middot; SEDE ${sesion.sede}</p>
            <p style="color: var(--text-main); font-weight: 600; margin-top: 8px;">CAJERO: ${sesion.usuario.toUpperCase()}</p>
        </div>
        <div class="header-meta">
            <span class="badge">${sesion.estado}</span>
            <p style="margin: 8px 0 0 0; font-weight: 600;">${fechaReporte}</p>
            <p style="margin: 2px 0 0 0; color: var(--text-muted);">${horaReporte}</p>
        </div>
    </div>

    <div class="summary-grid">
        <div class="card">
            <div class="card-label">Saldo Inicial</div>
            <div class="card-value">${fmt(sesion.monto_inicial)}</div>
        </div>
        <div class="card">
            <div class="card-label">Ingresos Totales</div>
            <div class="card-value value-success">+ ${fmt(sesion.ingresos_sistema)}</div>
        </div>
        <div class="card">
            <div class="card-label">Egresos Totales</div>
            <div class="card-value value-danger">- ${fmt(sesion.egresos_sistema)}</div>
        </div>
        <div class="card" style="background: white; border: 2px solid var(--primary);">
            <div class="card-label">Saldo de Cierre Real</div>
            <div class="card-value value-primary">${fmt(sesion.monto_final_real)}</div>
        </div>
    </div>

    ${sesion.descuadre !== 0 ? `
    <div style="margin-bottom: 30px; padding: 12px; border-radius: 8px; background: ${sesion.descuadre > 0 ? '#ecfdf5' : '#fef2f2'}; border: 1px solid ${sesion.descuadre > 0 ? '#10b981' : '#ef4444'};">
        <span style="font-weight: 700; color: ${sesion.descuadre > 0 ? '#065f46' : '#991b1b'};">
            ${sesion.descuadre > 0 ? 'SOBRANTE DETECTADO' : 'FALTANTE DETECTADO'}:
        </span>
        <span style="font-weight: 700; float: right; color: ${sesion.descuadre > 0 ? '#065f46' : '#991b1b'};">
            ${fmt(Math.abs(sesion.descuadre))}
        </span>
    </div>
    ` : ''}

    <!-- Recaudación -->
    <div class="section-title">
        <span>Recaudación de Pagos</span>
        <span style="font-size: 11px; color: var(--text-muted); font-weight: 400;">Total Documentos: ${recaudacion.length}</span>
    </div>
    <table>
        <thead>
            <tr>
                <th width="40" class="text-center">N°</th>
                <th width="50" class="text-center">Hora</th>
                <th width="120">Serie - Número</th>
                <th width="80">DNI</th>
                <th>Estudiante / Cliente</th>
                <th>Concepto</th>
                <th width="90" class="text-right">Monto</th>
            </tr>
        </thead>
        <tbody>
            ${recaudacion.map((item, idx) => `
                <tr>
                    <td class="text-center text-muted">${idx + 1}</td>
                    <td class="text-center">${item.hora}</td>
                    <td class="fw-bold">${item.serie_numero}</td>
                    <td>${item.dni}</td>
                    <td>${item.estudiante}</td>
                    <td style="font-size: 10px; color: var(--text-muted);">${item.conceptos}</td>
                    <td class="text-right fw-bold">${fmt(item.monto)}</td>
                </tr>
            `).join('')}
            <tr class="total-row">
                <td colspan="6" class="text-right">TOTAL RECAUDACIÓN</td>
                <td class="text-right">${fmt(totalRecaudacion)}</td>
            </tr>
        </tbody>
    </table>

    <!-- Otros Movimientos -->
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
        <div>
            <div class="section-title">Egresos / Gastos</div>
            ${gastos.length > 0 ? `
            <table>
                <thead>
                    <tr>
                        <th>Concepto / Categoría</th>
                        <th width="90" class="text-right">Monto</th>
                    </tr>
                </thead>
                <tbody>
                    ${gastos.map(item => `
                        <tr>
                            <td>
                                <div class="fw-bold">${item.concepto}</div>
                                <div style="font-size: 9px; color: var(--text-muted);">${item.categoria}</div>
                            </td>
                            <td class="text-right fw-bold value-danger">${fmt(item.monto)}</td>
                        </tr>
                    `).join('')}
                    <tr class="total-row">
                        <td class="text-right">TOTAL GASTOS</td>
                        <td class="text-right">${fmt(totalGastos)}</td>
                    </tr>
                </tbody>
            </table>
            ` : '<p style="color: var(--text-muted); italic">No se registraron egresos.</p>'}
        </div>

        <div>
            <div class="section-title">Depósitos a Bancos</div>
            ${depositos.length > 0 ? `
            <table>
                <thead>
                    <tr>
                        <th>Cuenta Destino</th>
                        <th width="90" class="text-right">Monto</th>
                    </tr>
                </thead>
                <tbody>
                    ${depositos.map(item => `
                        <tr>
                            <td>
                                <div class="fw-bold">${item.cuenta}</div>
                            </td>
                            <td class="text-right fw-bold value-primary">${fmt(item.monto)}</td>
                        </tr>
                    `).join('')}
                    <tr class="total-row">
                        <td class="text-right">TOTAL DEPÓSITOS</td>
                        <td class="text-right">${fmt(totalDepositos)}</td>
                    </tr>
                </tbody>
            </table>
            ` : '<p style="color: var(--text-muted); italic">No se registraron depósitos.</p>'}
        </div>
    </div>

    <div class="footer">
        <p>Generado por: ${sesion.usuario.toUpperCase()} &middot; SISTEMA DE GESTIÓN ACADÉMICA ORION</p>
        <p style="margin-top: 10px; opacity: 0.5;">Este documento es un reporte interno de movimientos de caja.</p>
    </div>
</body>
</html>
    `;
};
